import { GenericAbortSignal } from 'axios';

import { AICriteriaSet, AICriteriaSetOptions } from 'models/flexible/AICriteriaSet';
import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminAICriteriaSetCreateParams,
  AdminAICriteriaSetIndexFields,
  AdminAICriteriaSetIndexInclude,
  AdminAICriteriaSetShowFields,
  AdminAICriteriaSetShowInclude,
  AdminAICriteriaSetUpdateParams,
} from 'services/api/fields';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const aiCriteriaSetsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminAICriteriaSets', params] as const,
  show: (id: string) => ['adminAICriteriaSet', id] as const,
};

const URL = 'admin/ai_criteria_sets';

export type AdminAICriteriaSetIndexParams = BaseIndexQueryParams & {
  groupId?: string;
};

export async function indexAICriteriaSets<I extends AdminAICriteriaSetIndexInclude = never>(
  params: AdminAICriteriaSetIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<AICriteriaSet & AdminAICriteriaSetIndexFields<I>>> {
  return http
    .get<Paginated<AICriteriaSetOptions>>(URL, {
      params: { ...params, ...(includes?.length ? { include: includes.join(',') } : {}) },
      signal,
    })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new AICriteriaSet(x)),
    })) as Promise<Paginated<AICriteriaSet & AdminAICriteriaSetIndexFields<I>>>;
}

export async function showAICriteriaSet<I extends AdminAICriteriaSetShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: readonly I[]
): Promise<AICriteriaSet & AdminAICriteriaSetShowFields<I>> {
  return http
    .get<AICriteriaSetOptions>(`${URL}/${id}`, {
      params: { include: includes?.join(',') },
      signal,
    })
    .then((res) => new AICriteriaSet(res.data)) as Promise<AICriteriaSet & AdminAICriteriaSetShowFields<I>>;
}

export async function createAICriteriaSet(params: AdminAICriteriaSetCreateParams) {
  return http.post<AICriteriaSetOptions>(URL, params).then((res) => new AICriteriaSet(res.data));
}

export async function updateAICriteriaSet(params: AdminAICriteriaSetUpdateParams) {
  const { id, ...body } = params;
  return http.patch<AICriteriaSetOptions>(`${URL}/${id}`, body).then((res) => new AICriteriaSet(res.data));
}

export async function deleteAICriteriaSet(id: string) {
  return http.delete(`${URL}/${id}`);
}
